import React, { useState } from 'react'
import DefaultModal from './DefaultModal'

interface Props {
  isOpen: boolean
  onClose: () => void
  isLoading: boolean
  onConfirm: () => void
}

const CONFIRM_WORD = 'CLOSE'

/**
 * Customer-facing confirmation for permanently closing an account. Deliberately high-friction:
 * the customer must type the confirmation word before the destructive action is enabled. Closing
 * is irreversible — the account can no longer log in afterwards — so the copy is explicit about that.
 */
const CloseAccountModal: React.FC<Props> = ({
  isOpen,
  onClose,
  isLoading,
  onConfirm,
}) => {
  const [confirmText, setConfirmText] = useState('')

  const handleClose = () => {
    if (isLoading) return
    setConfirmText('')
    onClose()
  }

  const canConfirm = confirmText.trim().toUpperCase() === CONFIRM_WORD

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={handleClose}
      isLoading={isLoading}
      isLoadingText="Closing your account…"
      title="Close your account"
    >
      <div className="p-5 pt-0 flex flex-col gap-4">
        <p className="text-description">
          This will <strong>permanently close</strong> your account. You will be
          logged out and will <strong>no longer be able to log in</strong>. This
          cannot be undone.
        </p>
        <p className="text-description">
          For regulatory reasons we retain your account records after closure —
          your data is kept, but access is permanently removed.
        </p>
        <div className="flex flex-col gap-1">
          <label className="text-description text-sm" htmlFor="close-confirm">
            Type <strong>{CONFIRM_WORD}</strong> to confirm
          </label>
          <input
            id="close-confirm"
            className="input"
            value={confirmText}
            autoComplete="off"
            placeholder={CONFIRM_WORD}
            onChange={e => setConfirmText(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-3 mt-2">
          <button
            className="button-secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            className="button-danger"
            onClick={onConfirm}
            disabled={!canConfirm || isLoading}
          >
            Close my account
          </button>
        </div>
      </div>
    </DefaultModal>
  )
}

export default CloseAccountModal
